import { useSelector } from 'react-redux';
import { selectSweepFieldsInfo } from './sweepFieldsInfoReducer';
import { useCallback } from 'react';
import { SelectorValueTypes } from '../types/enums/SelectorValueTypes';
import isArray from 'lodash/isArray';
import { NestedSelectorPath } from '../components/common/NestedSelector/NestedSelector';
import { getNestedPath } from '../components/Automations/helper';
import last_ from 'lodash/last';
import { SweepCriterion, SweepCriterionWithLabels } from '../types/SweepCriterion';

// Gets the last id from an array of ids or a single id
const getIdOrLastIdFromArray = (ids: string | string[] | undefined) => {
  if (!ids) {
    return undefined;
  }
  return typeof ids === 'string' ? ids : last_(ids);
};

export const useSweepFieldsInfo = () => {
  const sweepFieldsInfo = useSelector(selectSweepFieldsInfo);

  const getFieldInfoFromId = useCallback(
    (id: string | string[]) => {
      const lastId = getIdOrLastIdFromArray(id);
      return lastId ? sweepFieldsInfo[lastId] : undefined;
    },
    [sweepFieldsInfo],
  );

  const getLabelsFromIds = useCallback(
    (ids: string[]) => ids.map((id) => sweepFieldsInfo[id]?.label || ''),
    [sweepFieldsInfo],
  );

  const getLabelFromIdsWithObjectType = useCallback(
    (ids: string[]) => {
      if (!ids || ids.length === 0) {
        return [];
      }
      const objectName = sweepFieldsInfo[ids[0]]?.objectName;
      return [objectName || '', ...ids.map((id) => sweepFieldsInfo[id]?.label || '')];
    },
    [sweepFieldsInfo],
  );

  const getSalesforceDisplayTypeFromFieldId = useCallback(
    (id?: string | string[]) => {
      const lastId = getIdOrLastIdFromArray(id);

      return lastId ? sweepFieldsInfo[lastId]?.sfDisplayType : undefined;
    },
    [sweepFieldsInfo],
  );

  const enrichCriteriaWithLabelsAndSfdcDisplayType = useCallback(
    (criteria: SweepCriterion): SweepCriterionWithLabels => {
      const _valueLabels =
        criteria.valueType === SelectorValueTypes.REFERENCE && isArray(criteria.value)
          ? getLabelFromIdsWithObjectType(criteria.value)
          : undefined;
      return {
        ...criteria,
        _fieldLabels: getLabelFromIdsWithObjectType(criteria._fieldIds),
        _valueLabels,
        _sfDisplayType: getSalesforceDisplayTypeFromFieldId(criteria._fieldIds),
      };
    },
    [getLabelFromIdsWithObjectType, getSalesforceDisplayTypeFromFieldId],
  );
  const removeLabelsFromCriteria = useCallback(
    (criteria: SweepCriterionWithLabels): SweepCriterion => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { _fieldLabels, _valueLabels, ...rest } = criteria;
      return rest;
    },
    [],
  );

  const getEnrichedNestedPath = useCallback(
    (_fieldIds: string | string[] | undefined): NestedSelectorPath => {
      if (_fieldIds == undefined) {
        return getNestedPath(undefined, undefined);
      }
      const _fieldIdsArray = isArray(_fieldIds) ? _fieldIds : [_fieldIds];
      const _fieldLabels = getLabelFromIdsWithObjectType(_fieldIdsArray);
      return getNestedPath(_fieldIds, _fieldLabels);
    },
    [getLabelFromIdsWithObjectType],
  );

  return {
    getLabelsFromIds,
    getLabelFromIdsWithObjectType,
    enrichCriteriaWithLabelsAndSfdcDisplayType,
    removeLabelsFromCriteria,
    getEnrichedNestedPath,
    getFieldInfoFromId,
    getSalesforceDisplayTypeFromFieldId,
    sweepFieldsInfo,
  };
};
